import {
  TeText,
  TeButton,
  TeBadge,
  TeCell,
  TeCheckbox,
  TeRow,
  TeTable,
  TeContainerSlim,
  TeContextMenu,
  TeContextMenuControl,
  TeContextMenuItem,
  TeTooltip,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { formatISO9075 } from 'date-fns';

import { CombinedUser, Status } from '../types';
import HeaderCell from '../HeaderCell';
import ProfileImage from '../../../components/ProfileImage';
import {
  buildProfileMap,
  resolveProfileDescription,
  resolveProfileDisplayName,
} from '../../../utils/profiles';
import { useAppService } from '../../../providers/app/app.context';

interface Props {
  users: CombinedUser[];
  selectedIds: string[];
  setSelectedIds: Dispatch<SetStateAction<string[]>>;
  setUserRemoveModalOpen: Dispatch<SetStateAction<boolean>>;
  selectedId: string;
  currentIdentityId?: string;
  onUserSelect: (id: string) => void;
  onUserEdit: (id: string) => void;
  onSortChange?: () => void;
  currentLoggedUserIsOwner?: boolean;
}

function UsersTable({
  users,
  selectedIds,
  setSelectedIds,
  setUserRemoveModalOpen,
  selectedId,
  currentIdentityId,
  onUserSelect,
  onUserEdit,
  onSortChange,
  currentLoggedUserIsOwner,
}: Props) {
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<string>('asc');
  const { t } = useTranslation('users');
  const { availableProfiles } = useAppService();
  const profileMap = useMemo(() => buildProfileMap(availableProfiles), [availableProfiles]);
  const sortedUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      if (sortKey === 'status') return (a.status ?? '').localeCompare(b.status ?? '', 'fi');
      const aName = a?.name ?? '';
      const bName = b?.name ?? '';
      return aName.localeCompare(bName ?? '', 'fi', { numeric: true });
    });
    if (sortDirection === 'desc') sorted.reverse();
    return sorted;
  }, [users, sortKey, sortDirection]);

  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < users.length;
  const isAllSelected = selectedIds.length === users.length;

  const toggleUserSelect = (selectedID: string) => {
    setSelectedIds((prev) =>
      prev.includes(selectedID) ? prev.filter((id) => id !== selectedID) : [...prev, selectedID]
    );
  };

  const toggleAll = () => {
    setSelectedIds(() => (isAllSelected ? [] : users.map(({ id }) => id)));
  };

  const handleSortChange = (key: string) => {
    const isSame = sortKey === key;
    if (!isSame) setSortKey(key);
    setSortDirection((prev) => {
      if (!isSame) return 'asc';
      return prev === 'asc' ? 'desc' : 'asc';
    });
    onSortChange?.();
  };

  const handleUserRemoveAction = (id: string) => {
    setSelectedIds([id]);
    setUserRemoveModalOpen(true);
  };

  return (
    <Table evenOdd nested staticHeader>
      <TeRow>
        <TeCell justifyContent="center" noPadding compact>
          <TeCheckbox
            checked={isIndeterminate || isAllSelected}
            indeterminate={isIndeterminate}
            techange={toggleAll}
            wrapperSize={40}
          />
        </TeCell>
        <HeaderCell
          onHeaderClick={handleSortChange}
          sortKey={sortKey}
          sortDirection={sortDirection}
          name={t('name')}
          cellKey="name"
        />
        <HeaderCell
          onHeaderClick={handleSortChange}
          sortKey={sortKey}
          sortDirection={sortDirection}
          name={t('status')}
          cellKey="status"
        />
        <HeaderCell
          onHeaderClick={handleSortChange}
          sortKey={sortKey}
          sortDirection={sortDirection}
          name={t('email')}
          cellKey="email"
        />
        <HeaderCell name={t('profiles')} />
        <HeaderCell
          onHeaderClick={handleSortChange}
          sortKey={sortKey}
          sortDirection={sortDirection}
          name={t('addedAt')}
          cellKey="addedAt"
        />
        <TeCell compact />
      </TeRow>

      {sortedUsers.map(
        ({ id, status, name, email, profileIds, createdAt, isOwner, identityId }) => {
          const selected = selectedIds.includes(id);
          const canRemove = !isOwner && identityId !== currentIdentityId;
          const canEdit =
            currentLoggedUserIsOwner || (identityId !== currentIdentityId && !isOwner);
          return (
            <TeRow key={id} active={selected || selectedId === id} teclick={() => onUserSelect(id)}>
              <TeCell justifyContent="center" noPadding compact>
                <TeCheckbox
                  checked={selected}
                  techange={() => toggleUserSelect(id)}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  wrapperSize={40}
                />
              </TeCell>
              <TeCell>
                <TeContainerSlim gap="xs" alignItems="center" padding={['xs', 0]}>
                  <ProfileImage email={email} size={32} />
                  <TeText weight={500}>{name}</TeText>
                  {isOwner && (
                    <TeBadge
                      size="s"
                      backgroundColor="background.inverse"
                      text={t('owner', { ns: 'common' })}
                    />
                  )}
                </TeContainerSlim>
              </TeCell>
              <TeCell>
                {status === Status.Invited && (
                  <TeBadge
                    size="s"
                    text={t('invited')}
                    backgroundColor="background.info.secondary"
                    textColor="text.primary"
                  />
                )}
              </TeCell>
              <TeCell>
                <TeText>{email}</TeText>
              </TeCell>
              <TeCell>
                <TeContainerSlim gap="xs" alignItems="center">
                  {profileIds.map((profileId) => {
                    const profile = profileMap.get(profileId);
                    const popKey = `${id}-${profileId}`;
                    if (!profile) {
                      return (
                        <Badge
                          size="s"
                          key={profileId}
                          text={profileId}
                          textColor="text.primary"
                          backgroundColor="background.quaternary"
                        />
                      );
                    }
                    return (
                      <React.Fragment key={profileId}>
                        <Badge
                          popkey={popKey}
                          size="s"
                          text={resolveProfileDisplayName(profile, t)}
                          backgroundColor="background.quaternary"
                          textColor="text.primary"
                        />
                        <TeTooltip for={popKey} text={resolveProfileDescription(profile, t)} />
                      </React.Fragment>
                    );
                  })}
                </TeContainerSlim>
              </TeCell>
              <TeCell>{createdAt && <TeText>{formatISO9075(new Date(createdAt))}</TeText>}</TeCell>
              <TeCell justifyContent="center" compact>
                <TeContextMenuControl contextId={`options-${id}`}>
                  <TeButton
                    icon="more_vert"
                    backgroundColor="transparent"
                    iconColor="icon.secondary"
                    size="s"
                  />
                </TeContextMenuControl>
                <TeContextMenu contextId={`options-${id}`} size="s">
                  <TeContextMenuItem
                    text={t('edit')}
                    teclick={() => onUserEdit(id)}
                    disabled={!canEdit}
                  />
                  <TeContextMenuItem
                    text={t('remove')}
                    teclick={() => handleUserRemoveAction(id)}
                    disabled={!canRemove}
                  />
                </TeContextMenu>
              </TeCell>
            </TeRow>
          );
        }
      )}
    </Table>
  );
}

export default UsersTable;

const Table = styled(TeTable)`
  border-radius: 0;
`;

const Badge = styled(TeBadge)`
  cursor: pointer;
`;
